import { useEffect, useRef } from "react";
import { useSession, organization, authClient } from "@/lib/auth-client";

// Silent org bootstrap. Runs once when a freshly-signed-up user lands
// without an activeOrganizationId. The user never sees this — it's the
// gap between "signup succeeded" and "list page renders." Mirrors
// interchange-web; an org with no loads simply renders an empty list.
//
// The word "organization" is deliberately never shown: a driver signs up
// and starts capturing loads. The org exists for the data model, not for
// them, so it is auto-named after them and never surfaced.
export function OrgBootstrap() {
  const { data: session, refetch } = useSession();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!session || ranRef.current) return;
    ranRef.current = true;

    void (async () => {
      const userName = session.user.name?.trim();
      const orgName = userName ? `${userName.split(" ")[0]}'s Loads` : "My Loads";
      const slug = `ps-${session.user.id.slice(0, 8)}-${Date.now().toString(36)}`;

      // Look before creating. This screen is reached whenever the session
      // has no activeOrganizationId — which is NOT the same as "this user
      // has no organization": a new device, a cleared session, or a
      // setActive that didn't stick all land here too. Creating
      // unconditionally is how one account ended up owning three identical
      // orgs. If a membership already exists, adopt it.
      const existing = await organization.list();
      const already = existing.data?.[0];
      if (already) {
        await organization.setActive({ organizationId: already.id });
        await refetch();
        return;
      }

      const created = await organization.create({ name: orgName, slug });
      if (created.error || !created.data) {
        // The server caps a user at one organization, so a create can also
        // fail because another tab won the race and made it first. Re-read
        // before giving up: if there's an org now, adopt it.
        const raced = await organization.list();
        const winner = raced.data?.[0];
        if (winner) {
          await organization.setActive({ organizationId: winner.id });
          await refetch();
          return;
        }
        console.error("[org-bootstrap] create failed", created.error);
        return;
      }
      await organization.setActive({ organizationId: created.data.id });
      // Better-Auth's create may not auto-set activeOrganizationId on the
      // session; refetch to pick up the change so App's gate falls through.
      await refetch();
    })();
  }, [session, refetch]);

  // Belt-and-suspenders: if anything wedges, allow the user to retry.
  return (
    <div className="min-h-dvh flex flex-col items-center justify-center gap-4 p-6 text-center">
      <div className="text-sm text-muted-foreground">Setting things up…</div>
      <button
        type="button"
        className="text-xs underline text-muted-foreground/70"
        onClick={() => authClient.signOut().then(() => window.location.assign("/auth"))}
      >
        Sign out
      </button>
    </div>
  );
}
